import { Injectable, computed, signal } from '@angular/core';
import { AvatarRemovalStatus, AvatarUploadStatus } from '../models/avatar-upload.model';
import { PasswordChangeStatus, PasswordFieldErrors } from '../models/password-change.model';
import { MyAccountStatus, UserAvatar, UserProfile } from '../models/user-profile.model';

@Injectable({ providedIn: 'root' })
export class MyAccountStateService {
  private readonly _profile = signal<UserProfile | null>(null);
  private readonly _status = signal<MyAccountStatus>('idle');
  private readonly _error = signal<string | null>(null);

  /** Object URL of the downloaded picture, or `null` while there is none to show. */
  private readonly _avatarUrl = signal<string | null>(null);

  /** Version of the picture currently held, so an unchanged one is not downloaded twice. */
  private readonly _avatarVersion = signal<string | null>(null);

  private readonly _avatarUploadStatus = signal<AvatarUploadStatus>('idle');
  private readonly _avatarUploadError = signal<string | null>(null);

  /**
   * Whether the picture bytes are being fetched from `GET user/avatar` right now.
   *
   * A plain flag rather than a status union like its neighbours: the download has
   * no outcome the UI acts on. The picture is decoration, so a failure falls back
   * to the initial badge exactly as "the account has none" does, and there is no
   * message to word for it.
   */
  private readonly _isDownloadingAvatar = signal(false);

  private readonly _avatarRemoveStatus = signal<AvatarRemovalStatus>('idle');
  private readonly _avatarRemoveError = signal<string | null>(null);

  private readonly _passwordStatus = signal<PasswordChangeStatus>('idle');

  /** Message of the last refused change, shown as a toast. */
  private readonly _passwordError = signal<string | null>(null);

  /** Per-field messages of the last `422`, shown under their inputs. */
  private readonly _passwordFieldErrors = signal<PasswordFieldErrors>({});

  readonly profile = this._profile.asReadonly();
  readonly status = this._status.asReadonly();
  readonly error = this._error.asReadonly();
  readonly avatarUrl = this._avatarUrl.asReadonly();
  readonly avatarVersion = this._avatarVersion.asReadonly();
  readonly avatarUploadStatus = this._avatarUploadStatus.asReadonly();
  readonly avatarUploadError = this._avatarUploadError.asReadonly();
  readonly isDownloadingAvatar = this._isDownloadingAvatar.asReadonly();
  readonly avatarRemoveStatus = this._avatarRemoveStatus.asReadonly();
  readonly avatarRemoveError = this._avatarRemoveError.asReadonly();
  readonly passwordStatus = this._passwordStatus.asReadonly();
  readonly passwordError = this._passwordError.asReadonly();
  readonly passwordFieldErrors = this._passwordFieldErrors.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly isLoaded = computed(() => this._status() === 'loaded');

  /** Whether the profile was already read from the API at least once. */
  readonly hasProfile = computed(() => this._profile() !== null);

  /** Whether a picture is being uploaded right now. */
  readonly isUploadingAvatar = computed(() => this._avatarUploadStatus() === 'uploading');

  /** Whether the picture is being dropped right now. */
  readonly isRemovingAvatar = computed(() => this._avatarRemoveStatus() === 'removing');

  /** Whether the password change is in flight. */
  readonly isSavingPassword = computed(() => this._passwordStatus() === 'saving');

  setLoading(): void {
    this._status.set('loading');
    this._error.set(null);
  }

  setProfile(profile: UserProfile): void {
    this._status.set('loaded');
    this._profile.set(profile);
    this._error.set(null);
  }

  setError(message: string): void {
    this._status.set('error');
    this._error.set(message);
  }

  /**
   * Publishes the downloaded picture as an object URL. This service owns that
   * URL's lifetime: the previous one is revoked here, otherwise the browser
   * would keep every downloaded blob alive for the whole session.
   */
  setAvatar(blob: Blob, version: string): void {
    this.revokeAvatarUrl();
    this._avatarUrl.set(URL.createObjectURL(blob));
    this._avatarVersion.set(version);
  }

  /**
   * Replaces the picture recorded on the stored profile after an upload, so the
   * new `version` is the one the next profile sync compares against — without a
   * second `GET user/profile` just to learn it.
   */
  setProfileAvatar(avatar: UserAvatar): void {
    const profile = this._profile();
    if (profile) {
      this._profile.set({ ...profile, avatar });
    }
  }

  /**
   * Records on the stored profile that the account has no picture any more, the
   * mirror of {@link setProfileAvatar}: the next profile sync then has no
   * version to compare against and stops trying to download the dropped one.
   */
  clearProfileAvatar(): void {
    const profile = this._profile();
    if (profile) {
      this._profile.set({ ...profile, avatar: null });
    }
  }

  setAvatarUploading(): void {
    this._avatarUploadStatus.set('uploading');
    this._avatarUploadError.set(null);
  }

  setAvatarUploaded(): void {
    this._avatarUploadStatus.set('uploaded');
    this._avatarUploadError.set(null);
  }

  /** Back to a clean slate — nothing uploading, nothing refused. */
  clearAvatarUpload(): void {
    this._avatarUploadStatus.set('idle');
    this._avatarUploadError.set(null);
  }

  /** Records why the upload was refused — by the client gate or by the API. */
  setAvatarUploadError(message: string): void {
    this._avatarUploadStatus.set('error');
    this._avatarUploadError.set(message);
  }

  /** The picture is on its way; the avatar draws a bone until it lands. */
  setAvatarDownloading(): void {
    this._isDownloadingAvatar.set(true);
  }

  /** The download settled — with bytes, with a failure, or by being superseded. */
  clearAvatarDownload(): void {
    this._isDownloadingAvatar.set(false);
  }

  setAvatarRemoving(): void {
    this._avatarRemoveStatus.set('removing');
    this._avatarRemoveError.set(null);
  }

  setAvatarRemoved(): void {
    this._avatarRemoveStatus.set('removed');
    this._avatarRemoveError.set(null);
  }

  /** Back to a clean slate — nothing removing, nothing refused. */
  clearAvatarRemoval(): void {
    this._avatarRemoveStatus.set('idle');
    this._avatarRemoveError.set(null);
  }

  /** Records why the API refused to drop the picture. */
  setAvatarRemoveError(message: string): void {
    this._avatarRemoveStatus.set('error');
    this._avatarRemoveError.set(message);
  }

  setPasswordSaving(): void {
    this._passwordStatus.set('saving');
    this._passwordError.set(null);
    this._passwordFieldErrors.set({});
  }

  setPasswordSaved(): void {
    this._passwordStatus.set('saved');
    this._passwordError.set(null);
    this._passwordFieldErrors.set({});
  }

  /**
   * Records why the change was refused: the message to toast plus, for a `422`,
   * the sentence the API worded for each field it turned down.
   */
  setPasswordError(message: string, fieldErrors: PasswordFieldErrors = {}): void {
    this._passwordStatus.set('error');
    this._passwordError.set(message);
    this._passwordFieldErrors.set(fieldErrors);
  }

  /** Back to a clean slate — nothing saving, nothing refused. */
  clearPasswordChange(): void {
    this._passwordStatus.set('idle');
    this._passwordError.set(null);
    this._passwordFieldErrors.set({});
  }

  /** Drops the picture — the account has none, or the download failed. */
  clearAvatar(): void {
    this.revokeAvatarUrl();
    this._avatarVersion.set(null);
  }

  reset(): void {
    this._profile.set(null);
    this._status.set('idle');
    this._error.set(null);
    this.clearAvatarUpload();
    this.clearAvatarRemoval();
    this.clearAvatarDownload();
    this.clearPasswordChange();
    this.clearAvatar();
  }

  private revokeAvatarUrl(): void {
    const current = this._avatarUrl();
    if (current) {
      URL.revokeObjectURL(current);
      this._avatarUrl.set(null);
    }
  }
}
